import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { App } from '@capacitor/app';
import { api } from '@/lib/api';
import { appLinkTarget } from '@/lib/appLinks';
import { isNativeApp } from '@/lib/platform';

/**
 * Opens a tapped GRUNDO link inside the installed app (#105): a shared
 * activity (`/aktivitas/<id>`) directly, a share code (`/s/<code>`) via the
 * public lookup of its activity. Signed in, the real activity screen shows;
 * signed out, the router's public activity view.
 *
 * Must sit under the router (uses `useNavigate`). Covers both the cold start
 * (`getLaunchUrl`) and a link tapped while the app runs (`appUrlOpen`).
 */
export function AppLinkActions() {
  const navigate = useNavigate();
  const lastHandled = useRef<string | null>(null);

  useEffect(() => {
    if (!isNativeApp()) return;

    const open = (url: string) => {
      // A cold start can report the same address through both channels.
      if (lastHandled.current === url) return;
      const target = appLinkTarget(url);
      if (!target) return;
      lastHandled.current = url;
      if (target.kind === 'activity') {
        navigate(`/aktivitas/${target.id}`);
        return;
      }
      api
        .publicShareTarget(target.code)
        .then(({ activityId }) => navigate(`/aktivitas/${activityId}`))
        .catch(() => navigate('/'));
    };

    void App.getLaunchUrl().then((launch) => {
      if (launch?.url) open(launch.url);
    });
    const listener = App.addListener('appUrlOpen', ({ url }) => open(url));
    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [navigate]);

  return null;
}
